import { useEffect, useState, useSyncExternalStore } from 'react';

import { ALARM_SOUNDS } from './alarmSounds';

const BEEP_HZ = [880, 660] as const;
const BEEP_MS = 350;
const CYCLE_MS = 1_000;
const GAIN = 0.15;
const VIBRATE_MS = BEEP_MS;
const TOUCH = '(pointer: coarse)';
const UNLOCK_EVENTS = ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown'] as const;

type AudioContextCtor = typeof AudioContext;

const audioContextCtor = (): AudioContextCtor | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
};

const sharedContexts = new WeakMap<AudioContextCtor, AudioContext>();

const sharedContextOf = (Ctor: AudioContextCtor): AudioContext => {
  const existing = sharedContexts.get(Ctor);
  if (existing !== undefined && existing.state !== 'closed') return existing;
  const made = new Ctor();
  sharedContexts.set(Ctor, made);
  return made;
};

const isTouchScreen = (): boolean => typeof window.matchMedia === 'function' && window.matchMedia(TOUCH).matches;

const vibrate = (pattern: number): void => {
  navigator.vibrate?.(pattern);
};

const clipsByContext = new WeakMap<AudioContext, Map<string, AudioBuffer>>();
const loadingContexts = new WeakSet<AudioContext>();

const loadClips = (context: AudioContext): void => {
  if (ALARM_SOUNDS.length === 0 || loadingContexts.has(context)) return;
  loadingContexts.add(context);
  const buffers = new Map<string, AudioBuffer>();
  clipsByContext.set(context, buffers);
  ALARM_SOUNDS.forEach((url) => {
    void (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) return;
        buffers.set(url, await context.decodeAudioData(await response.arrayBuffer()));
      } catch {
        // the synthesised beep stands in for a clip that will not load
      }
    })();
  });
};

const clipAt = (context: AudioContext, turn: number): AudioBuffer | undefined => {
  const buffers = clipsByContext.get(context);
  if (buffers === undefined) return undefined;
  const ready = ALARM_SOUNDS.map((url) => buffers.get(url)).filter((buffer): buffer is AudioBuffer => buffer !== undefined);
  return ready.length === 0 ? undefined : ready[turn % ready.length];
};

/**
 * How long to wait before the next beat: long enough to let the clip finish, so
 * a clip of any length is heard whole. A buffer with no usable duration — only a
 * stub ever has one — falls back to the beep cadence.
 */
const beatAfter = (clip: AudioBuffer): number => {
  const ms = clip.duration * 1_000;
  return Number.isFinite(ms) && ms > 0 ? ms : CYCLE_MS;
};

const playClip = (context: AudioContext, clip: AudioBuffer): AudioBufferSourceNode => {
  const source = context.createBufferSource();
  const gain = context.createGain();
  source.buffer = clip;
  gain.gain.value = GAIN;
  source.connect(gain).connect(context.destination);
  source.start();
  return source;
};

const beep = (context: AudioContext, hz: number): void => {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.frequency.value = hz;
  gain.gain.value = GAIN;
  oscillator.connect(gain).connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + BEEP_MS / 1000);
};

type AudioStatus = 'idle' | 'playing' | 'held' | 'muted' | 'unavailable';

const statusOf = (context: AudioContext | null): AudioStatus => {
  if (context === null) return 'unavailable';
  return context.state === 'running' ? 'playing' : 'held';
};

const restingStatus = (): AudioStatus => (audioContextCtor() === null ? 'unavailable' : 'idle');

interface AlarmEngine {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => AudioStatus;
  readonly start: () => void;
  readonly stop: () => void;
  readonly mute: () => void;
}

const createAlarmEngine = (): AlarmEngine => {
  const listeners = new Set<() => void>();
  let status: AudioStatus = restingStatus();
  let teardown: (() => void) | null = null;
  let silence: (() => void) | null = null;

  const set = (next: AudioStatus): void => {
    if (next === status) return;
    status = next;
    listeners.forEach((listener) => listener());
  };

  const start = (): void => {
    if (teardown !== null) return;
    const Ctor = audioContextCtor();
    const context = Ctor === null ? null : sharedContextOf(Ctor);
    const touch = isTouchScreen();
    const readStatus = () => {
      if (status !== 'muted') set(statusOf(context));
    };
    const unlock = () => {
      if (context !== null && context.state !== 'running') void context.resume().catch(() => undefined);
    };
    context?.addEventListener('statechange', readStatus);
    UNLOCK_EVENTS.forEach((type) => window.addEventListener(type, unlock));
    if (context !== null) loadClips(context);
    let turn = 0;
    let playing: AudioBufferSourceNode | null = null;
    const hush = () => {
      playing?.stop();
      playing = null;
    };
    let timer: number | null = null;
    const ring = () => {
      if (touch) vibrate(VIBRATE_MS);
      let wait = CYCLE_MS;
      if (context?.state === 'running') {
        hush();
        const clip = clipAt(context, turn);
        if (clip === undefined) beep(context, BEEP_HZ[turn % BEEP_HZ.length]);
        else {
          playing = playClip(context, clip);
          wait = beatAfter(clip);
        }
        turn += 1;
      }
      timer = window.setTimeout(ring, wait);
    };
    unlock();
    readStatus();
    ring();
    const quiet = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      hush();
      if (touch) vibrate(0);
    };
    silence = () => {
      quiet();
      set('muted');
    };
    teardown = () => {
      quiet();
      UNLOCK_EVENTS.forEach((type) => window.removeEventListener(type, unlock));
      context?.removeEventListener('statechange', readStatus);
    };
  };

  const stop = (): void => {
    teardown?.();
    teardown = null;
    silence = null;
    set(restingStatus());
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => status,
    start,
    stop,
    mute: () => silence?.(),
  };
};

export type AlarmState = 'ringing' | 'blocked' | 'muted' | 'unavailable' | 'off';

export interface AlarmTone {
  readonly state: AlarmState;
  readonly mute: () => void;
}

const stateOf = (active: boolean, audio: AudioStatus): AlarmState => {
  if (!active) return 'off';
  switch (audio) {
    case 'unavailable':
      return 'unavailable';
    case 'muted':
      return 'muted';
    case 'held':
      return 'blocked';
    case 'idle':
    case 'playing':
      return 'ringing';
  }
};

export const useAlarmTone = (active: boolean): AlarmTone => {
  const [engine] = useState(createAlarmEngine);
  const audio = useSyncExternalStore(engine.subscribe, engine.getSnapshot, engine.getSnapshot);

  useEffect(() => {
    if (!active) return undefined;
    engine.start();
    return engine.stop;
  }, [active, engine]);

  return { state: stateOf(active, audio), mute: engine.mute };
};
